// src/services/nftFetcher/index.js

import { supportedChains } from '../../chains';
import { fetchNFTs as fetchBitrockNFTs } from './bitrockNFTFetcher';
import { fetchNFTs as fetchEthereumNFTs } from './ethereumNFTFetcher';
import { fetchNFTs as fetchBaseNFTs } from './baseNFTFetcher';
import { fetchNFTs as fetchArbitrumNFTs } from './arbitrumNFTFetcher';
import { fetchNFTs as fetchAvalancheNFTs } from './avalancheNFTFetcher';
import { fetchNFTs as fetchPolygonNFTs } from './polygonNFTFetcher';
import { fetchNFTs as fetchCeloNFTs } from './celoNFTFetcher';
import { fetchNFTs as fetchOptimismNFTs } from './optimismNFTFetcher';
import { fetchNFTs as fetchCronosNFTs } from './cronosNFTFetcher';
import { fetchNFTs as fetchOmaxNFTs } from './omaxNFTFetcher';
import { fetchNFTs as fetchInfinaeonNFTs } from './infinaeonNFTFetcher';
import { fetchNFTs as fetchTaraxaNFTs } from './taraxaNFTFetcher';
import { fetchNFTs as fetchBscNFTs } from './bscNFTFetcher';

// Map of chain keys to their specific NFT fetcher implementations
const NFT_FETCHERS = {
  'bitrock': fetchBitrockNFTs,
  'ethereum': fetchEthereumNFTs,
  'base': fetchBaseNFTs,
  'arbitrum': fetchArbitrumNFTs,
  'avalanche': fetchAvalancheNFTs,
  'polygon': fetchPolygonNFTs,
  'celo': fetchCeloNFTs,
  'optimism': fetchOptimismNFTs,
  'cronos': fetchCronosNFTs,
  'omax': fetchOmaxNFTs,
  'infinaeon': fetchInfinaeonNFTs,
  'taraxa': fetchTaraxaNFTs,
  'bsc': fetchBscNFTs,
};

/**
 * Generic NFT fetcher implementation suitable for chains 
 * that follow the ERC-721 contract standard but might have
 * different explorer APIs
 */
const fetchGenericNFTs = async (walletAddress, apiEndpoint, responseMapper) => {
  try {
    // Replace {address} placeholder in API endpoint
    const url = apiEndpoint.replace('{address}', walletAddress);
    
    const response = await fetch(url);
    const data = await response.json();
    
    // Use the provided mapper function to transform the API response
    return responseMapper(data);
  } catch (error) {
    console.error("Error fetching NFTs:", error);
    return [];
  }
};

/**
 * Fetch NFTs for a given wallet address based on the chain.
 * This function handles chain-specific NFT fetching logic
 * and will work with any chain that has the appropriate configuration.
 *
 * @param {string} chainKey - The key of the chain (e.g., 'bitrock' or 'ethereum')
 * @param {string} walletAddress - The wallet address for which to fetch NFTs
 * @returns {Promise<Array>} - A promise that resolves to an array of NFTs
 */
export const fetchNFTs = async (chainKey, walletAddress) => {
  try {
    // First check if we have a custom fetcher implementation for this chain
    if (NFT_FETCHERS[chainKey]) {
      return await NFT_FETCHERS[chainKey](walletAddress);
    }
    
    // For chains without a custom fetcher, check if they have a generic API endpoint
    const chainConfig = supportedChains[chainKey];
    if (!chainConfig) {
      throw new Error(`Unsupported chain: ${chainKey}`);
    }
    
    // Check if this chain has a nftApiEndpoint and responseMapper
    if (chainConfig.nftApiEndpoint && chainConfig.nftResponseMapper) {
      return fetchGenericNFTs(
        walletAddress, 
        chainConfig.nftApiEndpoint,
        chainConfig.nftResponseMapper
      );
    }
    
    // If no custom fetcher or generic API endpoint is available
    console.warn(`No NFT fetcher defined for chain: ${chainKey}`);
    return [];
    
  } catch (error) {
    console.error(`Error fetching NFTs for ${chainKey}:`, error);
    return [];
  }
};

/**
 * Register a new NFT fetcher implementation for a specific chain
 * 
 * @param {string} chainKey - The key of the chain
 * @param {function} fetcherFunction - The NFT fetcher implementation
 */
export const registerNFTFetcher = (chainKey, fetcherFunction) => {
  if (typeof fetcherFunction !== 'function') {
    throw new Error('fetcherFunction must be a function');
  }
  
  NFT_FETCHERS[chainKey] = fetcherFunction;
  console.log(`Registered NFT fetcher for ${chainKey}`);
};

/**
 * Get available NFT chains
 * @returns {Array<string>} - Array of chain keys that support NFT fetching
 */
export const getAvailableNFTChains = () => {
  const customChains = Object.keys(NFT_FETCHERS);
  const configuredChains = Object.keys(supportedChains).filter(chainKey => {
    const config = supportedChains[chainKey];
    return config.nftApiEndpoint && config.nftResponseMapper;
  });
  
  return [...new Set([...customChains, ...configuredChains])];
};